import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, Modal, TouchableOpacity, Linking, ScrollView, TextInput, Platform, useWindowDimensions } from 'react-native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useTheme, ThemeColors } from '../../context/ThemeContext';
import { HELP_AREAS, HELP_SUPPORT, HELP_TOPICS, HelpPlatform, HelpTopic } from '../../help/helpTopics';
import { groupByArea, searchHelpTopics } from '../../help/helpSearch';
import { HelpTopicBody } from './HelpGuideParts';

/**
 * The Help centre (Settings > Support > Help centre): the school office's phone and hours, then a
 * guide to every feature - short topics by feature, with a search box. Tap a topic to open its steps.
 * The content is data (help/helpTopics.ts); this sheet only shows it.
 */

interface Props {
  visible: boolean;
  onClose: () => void;
}

export function HelpCenterModal({ visible, onClose }: Props) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const platform: HelpPlatform = Platform.OS === 'web' ? 'web' : 'phone';
  // On a phone-width screen the call button goes under the hours, full width.
  const narrow = useWindowDimensions().width < 520;
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  // Each time it opens: an empty search and every topic closed.
  useEffect(() => {
    if (visible) { setQuery(''); setOpenId(null); }
  }, [visible]);

  const searching = query.trim().length > 0;
  const results = useMemo(() => searchHelpTopics(HELP_TOPICS, query, platform), [query, platform]);
  const groups = useMemo(() => groupByArea(results, HELP_AREAS), [results]);

  const topicRow = (topic: HelpTopic, showArea: boolean, last: boolean) => {
    const open = openId === topic.id;
    return (
      <View key={topic.id} style={[styles.topic, !last && styles.topicDivider]}>
        <TouchableOpacity style={styles.topicHead} onPress={() => setOpenId(open ? null : topic.id)}
          accessibilityRole="button" accessibilityState={{ expanded: open }} testID={`help-topic-${topic.id}`}>
          <View style={styles.fill}>
            {showArea && <Text style={styles.topicArea}>{topic.area}</Text>}
            <Text style={styles.topicTitle}>{topic.title}</Text>
          </View>
          <FeatherIcon name={open ? 'chevron-up' : 'chevron-down'} size={18} color={colors.textMuted} />
        </TouchableOpacity>
        {open && <HelpTopicBody topic={topic} platform={platform} colors={colors} />}
      </View>
    );
  };

  return (
    <Modal visible={visible} transparent animationType={platform === 'web' ? 'fade' : 'slide'} onRequestClose={onClose}>
      <View style={[styles.overlay, platform === 'web' && styles.overlayWeb]}>
        <TouchableOpacity style={StyleSheet.absoluteFill} activeOpacity={1} onPress={onClose}
          accessibilityRole="button" accessibilityLabel="Close help" />
        <View style={[styles.sheet, platform === 'web' && styles.sheetWeb]} testID="help-guide">
          <View style={styles.header}>
            <View style={styles.fill}>
              <Text style={styles.title} accessibilityRole="header">Help centre</Text>
              <Text style={styles.subtitle}>How to use the app, step by step</Text>
            </View>
            <TouchableOpacity onPress={onClose} style={styles.closeBtn} accessibilityRole="button" accessibilityLabel="Close help"
              testID="help-close">
              <FeatherIcon name="x" size={20} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>

          <View style={[styles.support, narrow && styles.supportNarrow]} testID="help-support">
            <View style={narrow ? undefined : styles.fill}>
              <Text style={styles.supportTitle}>School office</Text>
              {HELP_SUPPORT.hours.map(line => (
                <View key={line} style={styles.hoursRow}>
                  <FeatherIcon name="clock" size={12} color={colors.warning} />
                  <Text style={styles.hoursLine}>{line}</Text>
                </View>
              ))}
            </View>
            <TouchableOpacity style={styles.callBtn} onPress={() => Linking.openURL(HELP_SUPPORT.tel)}
              accessibilityRole="button" accessibilityLabel={`Call the school office on ${HELP_SUPPORT.phone}`} testID="help-call">
              <FeatherIcon name="phone" size={14} color="#FFFFFF" />
              <Text style={styles.callBtnText}>{HELP_SUPPORT.phone}</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.search}>
            <FeatherIcon name="search" size={16} color={colors.textMuted} />
            <TextInput
              value={query}
              onChangeText={setQuery}
              placeholder="Search help, e.g. leave, top up, menu"
              placeholderTextColor={colors.textMuted}
              style={styles.searchInput}
              autoCorrect={false}
              autoCapitalize="none"
              returnKeyType="search"
              accessibilityLabel="Search help"
              testID="help-search"
            />
            {searching && (
              <TouchableOpacity onPress={() => setQuery('')} accessibilityRole="button" accessibilityLabel="Clear search"
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                <FeatherIcon name="x-circle" size={16} color={colors.textMuted} />
              </TouchableOpacity>
            )}
          </View>

          <ScrollView style={styles.fill} contentContainerStyle={styles.listContent} keyboardShouldPersistTaps="handled">
            {searching ? (
              results.length === 0 ? (
                <Text style={styles.empty} testID="help-no-results">
                  {`Nothing matches "${query.trim()}". Try another word, or call the school office.`}
                </Text>
              ) : (
                <>
                  <Text style={styles.count}>{`${results.length} topic${results.length === 1 ? '' : 's'}`}</Text>
                  <View style={styles.card}>
                    {results.map((t, i) => topicRow(t, true, i === results.length - 1))}
                  </View>
                </>
              )
            ) : (
              groups.map(g => (
                <View key={g.area} style={styles.group}>
                  <Text style={styles.areaTitle}>{g.area.toUpperCase()}</Text>
                  <View style={styles.card}>
                    {g.topics.map((t, i) => topicRow(t, false, i === g.topics.length - 1))}
                  </View>
                </View>
              ))
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  fill: { flex: 1 },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.45)',
    justifyContent: 'flex-end',
  },
  overlayWeb: { justifyContent: 'center', alignItems: 'center', padding: 16 },
  sheet: {
    backgroundColor: colors.background,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    height: '92%',
    paddingTop: 16,
    overflow: 'hidden',
  },
  sheetWeb: { width: '100%', maxWidth: 640, height: '88%', borderRadius: 20 },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, marginBottom: 12, gap: 12 },
  title: { fontSize: 18, fontWeight: '800', color: colors.textPrimary },
  subtitle: { fontSize: 12.5, color: colors.textSecondary, marginTop: 2 },
  closeBtn: { padding: 6 },
  support: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginHorizontal: 16,
    padding: 12,
    borderRadius: 14,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
  },
  supportNarrow: { flexDirection: 'column', alignItems: 'stretch', gap: 10 },
  supportTitle: { fontSize: 13, fontWeight: '800', color: colors.textPrimary, marginBottom: 4 },
  hoursRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 2 },
  hoursLine: { fontSize: 12, color: colors.textSecondary, flexShrink: 1 },
  callBtn: {
    backgroundColor: colors.primary,
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  callBtnText: { color: '#FFFFFF', fontSize: 13, fontWeight: '700' },
  search: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginHorizontal: 16,
    marginTop: 12,
    marginBottom: 8,
    paddingHorizontal: 12,
    borderRadius: 12,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
  },
  searchInput: { flex: 1, fontSize: 14, color: colors.textPrimary, paddingVertical: 10 },
  listContent: { paddingHorizontal: 16, paddingBottom: 32, gap: 14 },
  group: { gap: 6 },
  areaTitle: { fontSize: 11, fontWeight: '800', color: colors.textMuted, letterSpacing: 0.6 },
  card: {
    backgroundColor: colors.surface,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: colors.border,
    paddingHorizontal: 14,
  },
  topic: { paddingVertical: 2 },
  topicDivider: { borderBottomWidth: 1, borderBottomColor: colors.divider },
  topicHead: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 12 },
  topicArea: { fontSize: 11, fontWeight: '700', color: colors.primary, marginBottom: 2 },
  topicTitle: { fontSize: 14, fontWeight: '700', color: colors.textPrimary },
  count: { fontSize: 12, color: colors.textSecondary },
  empty: { fontSize: 13.5, lineHeight: 20, color: colors.textSecondary, paddingVertical: 12 },
});
